/**
 * foamBomExcel.ts
 *
 * Excel Import / Export utilities for the quote-compatible packaging BOM.
 */
import * as XLSX from 'xlsx'
import { readSafeWorkbook } from '@/lib/safeWorkbook'
import type { FoamBomLine } from './types/foamBomTypes'
import { createBlankFoamBomLine } from './types/foamBomTypes'

export const FOAM_BOM_HEADERS = [
  'STT',
  'Tên hàng',
  'KT1',
  'KT2',
  'KT3',
  'KT4',
  'Định lượng',
  'ĐVT',
  'SL',
  'Ghi chú',
]

function normalizeHeader(h: unknown): string {
  if (h === null || h === undefined) return ''
  return String(h)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
}

function parseNumber(val: unknown): number | null {
  if (val === null || val === undefined || val === '') return null
  if (typeof val === 'number') return isNaN(val) ? null : val
  const cleaned = String(val).replace(/,/g, '.').replace(/[^\d.-]/g, '').trim()
  if (!cleaned) return null
  const num = parseFloat(cleaned)
  return isNaN(num) ? null : num
}

/**
 * Excel đổi "30/10" thành ngày (số serial 46325 = 30/10/2026) khi người nhập không để ô dạng văn bản.
 * Số serial trong khoảng ngày hợp lý trong ô kích thước được đổi lại thành "ngày/tháng".
 */
function dateSerialToDimension(num: number): string | null {
  if (!Number.isInteger(num) || num < 40000 || num > 60000) return null
  const d = new Date(Date.UTC(1899, 11, 30) + num * 86400000)
  return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`
}

function parseDimension(val: unknown): number | string | null {
  if (val === null || val === undefined || val === '') return null
  if (typeof val === 'number') {
    if (isNaN(val)) return null
    return dateSerialToDimension(val) ?? val
  }
  const str = String(val).trim()
  if (!str) return null
  if (/^\d{5}$/.test(str)) {
    const converted = dateSerialToDimension(Number(str))
    if (converted) return converted
  }
  const num = parseNumber(str)
  if (num !== null && String(num) === str.replace(/,/g, '.')) return num
  return str
}

export function exportFoamBomToExcel(
  productCode: string,
  assemblyLabel: string,
  packagingLabel: string,
  lines: FoamBomLine[],
): void {
  const aoa: any[][] = [FOAM_BOM_HEADERS]

  lines.forEach((l, idx) => {
    aoa.push([
      idx + 1,
      l.item_name || l.loai || '',
      l.kt1 !== null && l.kt1 !== undefined ? l.kt1 : '',
      l.kt2 !== null && l.kt2 !== undefined ? l.kt2 : '',
      l.kt3 !== null && l.kt3 !== undefined ? l.kt3 : '',
      l.kt4 !== null && l.kt4 !== undefined ? l.kt4 : '',
      l.dinh_luong !== null && l.dinh_luong !== undefined ? l.dinh_luong : '',
      l.dvt || '',
      l.sl ?? 1,
      l.ghi_chu || '',
    ])
  })

  const ws = XLSX.utils.aoa_to_sheet(aoa)

  // Set column widths
  ws['!cols'] = [
    { wch: 6 }, // STT
    { wch: 30 }, // Tên hàng
    { wch: 10 }, // KT1
    { wch: 10 }, // KT2
    { wch: 10 }, // KT3
    { wch: 10 }, // KT4
    { wch: 14 }, // Định lượng
    { wch: 8 }, // ĐVT
    { wch: 8 }, // SL
    { wch: 30 }, // Ghi chú
  ]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'BOM Đóng Gói')

  const safeCode = (productCode || 'PRODUCT').replace(/[^a-zA-Z0-9_-]/g, '_')
  const safeAsm = (assemblyLabel || '').replace(/[^a-zA-Z0-9_-]/g, '_')
  const safePkg = (packagingLabel || '').replace(/[^a-zA-Z0-9_-]/g, '_')
  const filename = `BOM_Dong_Goi_${safeCode}_${safeAsm}_${safePkg}.xlsx`

  XLSX.writeFile(wb, filename)
}

export function parseFoamBomExcel(data: ArrayBuffer): FoamBomLine[] {
  const wb = readSafeWorkbook(data)
  const firstSheetName = wb.SheetNames[0]
  if (!firstSheetName) return []

  const ws = wb.Sheets[firstSheetName]
  const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })

  if (rows.length === 0) return []

  // Find header row
  let headerRowIndex = -1
  let colLoai = -1
  let colKt1 = -1
  let colKt2 = -1
  let colKt3 = -1
  let colKt4 = -1
  let colDinhLuong = -1
  let colDvt = -1
  let colSl = -1
  let colGhiChu = -1

  for (let r = 0; r < Math.min(rows.length, 10); r++) {
    const row = rows[r]
    if (!Array.isArray(row)) continue

    let matched = 0
    row.forEach((cell, c) => {
      const norm = normalizeHeader(cell)
      if (norm === 'ten hang' || norm === 'ten vat tu' || norm.includes('ten vat tu') || norm === 'vat tu' || norm === 'material') {
        colLoai = c
        matched++
      } else if (norm === 'kt1' || norm === 'dai' || norm === 'length' || norm === 'l') {
        colKt1 = c
        matched++
      } else if (norm === 'kt2' || norm === 'rong' || norm === 'width' || norm === 'w') {
        colKt2 = c
        matched++
      } else if (norm === 'kt3' || norm === 'cao' || norm === 'day' || norm === 'height' || norm === 'thickness' || norm === 't' || norm === 'h') {
        colKt3 = c
        matched++
      } else if (norm === 'kt4') {
        colKt4 = c
        matched++
      } else if (norm.includes('dinh luong') || norm.includes('density') || norm.includes('ty trong') || norm === 'kieu') {
        colDinhLuong = c
        matched++
      } else if (norm === 'dvt' || norm === 'unit' || norm.includes('don vi')) {
        colDvt = c
        matched++
      } else if (norm === 'sl' || norm.includes('so luong') || norm === 'qty') {
        colSl = c
        matched++
      } else if (norm.includes('ghi chu') || norm === 'note' || norm === 'notes') {
        colGhiChu = c
        matched++
      }
    })

    // Second pass for ambiguous 'loai':
    // If 'colLoai' was already assigned (e.g. from 'Tên vật tư'), 'loai' refers to colDinhLuong.
    // If 'colLoai' was NOT assigned, 'loai' refers to colLoai (legacy).
    row.forEach((cell, c) => {
      const norm = normalizeHeader(cell)
      if (norm === 'loai' || norm === 'kind' || norm === 'phan loai') {
        if (colLoai === -1) {
          colLoai = c
          matched++
        } else if (colDinhLuong === -1 && c !== colLoai) {
          colDinhLuong = c
          matched++
        }
      }
    })

    if (matched >= 2) {
      headerRowIndex = r
      break
    }
  }

  // If header wasn't found by match, fallback to row 0 with default index positions
  if (headerRowIndex === -1) {
    headerRowIndex = 0
    colLoai = 1
    colKt1 = 2
    colKt2 = 3
    colKt3 = 4
    colDinhLuong = 5
    colDvt = 6
    colSl = 7
    colGhiChu = 8
  }

  const result: FoamBomLine[] = []

  for (let r = headerRowIndex + 1; r < rows.length; r++) {
    const row = rows[r]
    if (!Array.isArray(row)) continue

    const loai = colLoai >= 0 ? String(row[colLoai] || '').trim() : ''
    const kt1 = colKt1 >= 0 ? parseDimension(row[colKt1]) : null
    const kt2 = colKt2 >= 0 ? parseDimension(row[colKt2]) : null
    const kt3 = colKt3 >= 0 ? parseDimension(row[colKt3]) : null
    const kt4 = colKt4 >= 0 ? parseDimension(row[colKt4]) : null
    const dinh_luong = colDinhLuong >= 0 ? parseDimension(row[colDinhLuong]) : null
    const dvt = colDvt >= 0 ? String(row[colDvt] || '').trim() : 'Cái'
    const sl = colSl >= 0 ? (parseNumber(row[colSl]) ?? 1) : 1
    const ghi_chu = colGhiChu >= 0 ? String(row[colGhiChu] || '').trim() : ''

    // Skip entirely empty row
    if (!loai && kt1 === null && kt2 === null && kt3 === null && kt4 === null && dinh_luong === null && !ghi_chu) {
      continue
    }

    const newLine = createBlankFoamBomLine()
    newLine.item_name = loai
    // Legacy workbook import remains supported; semantic conversion is deferred to the legacy reader.
    if (colKt4 < 0) newLine.loai = loai || 'Xốp tấm'
    newLine.kt1 = kt1
    newLine.kt2 = kt2
    newLine.kt3 = kt3
    newLine.kt4 = kt4
    newLine.dinh_luong = dinh_luong
    newLine.dvt = dvt || 'Pcs'
    newLine.sl = sl > 0 ? sl : 1
    newLine.ghi_chu = ghi_chu

    result.push(newLine)
  }

  return result
}
